import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { CompleteKundali, Language } from '../types/kundali';
import { formatDate } from '../utils/dateUtils';
import { maskSensitiveContactInfo } from '../utils/dlpService';
import {
  ForensicDiagnosticHashes,
  TwoNativeTwoQuestionVerificationResult
} from '../utils/dynamicAnalysisEngine';
import { generateCanonicalAstrologicalAnalysis as generateDynamicAstrologicalAnalysis } from '../utils/dashaGocharSynthesisEngine';
import {
  Sparkles,
  MessageSquare,
  Send,
  Bot,
  User,
  RefreshCw,
  BookOpen,
  AlertCircle,
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ShieldCheck,
  Languages,
  Info,
  CheckCircle2,
  Scale,
  Cpu,
  Fingerprint,
  Layers,
  Terminal,
  Activity
} from 'lucide-react';

interface GeminiAstrologerProps {
  kundali: CompleteKundali;
  lang?: Language;
  initialPrompt?: string;
}

interface ChatMessage {
  sender: 'user' | 'astrologer';
  text: string;
}

const SUPPORTED_AI_LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'Malayalam', native: 'മലയാളം' },
  { code: 'pa', label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'sa', label: 'Sanskrit', native: 'संस्कृतम्' }
];

export const GeminiAstrologer: React.FC<GeminiAstrologerProps> = ({ kundali, lang = 'en', initialPrompt }) => {
  const [activeAiLang, setActiveAiLang] = useState<string>(lang || 'en');
  const [analysisText, setAnalysisText] = useState<string | null>(null);
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [showAllSubPeriods, setShowAllSubPeriods] = useState(false);
  const [showEngineDirectives, setShowEngineDirectives] = useState(false);
  const [showForensicDebug, setShowForensicDebug] = useState(false);
  const [verificationResult, setVerificationResult] = useState<TwoNativeTwoQuestionVerificationResult | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasSentInitialPrompt, setHasSentInitialPrompt] = useState(false);
  const [activeForensicHashes, setActiveForensicHashes] = useState<ForensicDiagnosticHashes | null>(null);

  // Synchronize initial prop lang if changed
  useEffect(() => {
    if (lang) {
      setActiveAiLang(lang);
    }
  }, [lang]);

  const isHindi = activeAiLang === 'hi';

  // Compute active Mahadasha and Antardashas with precise dates
  const currentActiveDasha = kundali.dashas?.find((d) => d.isCurrent) || kundali.dashas?.[0];
  const currentSubDasha = currentActiveDasha?.antardashas?.find((a) => a.isCurrent);
  const currentSubDashaIndex = currentActiveDasha?.antardashas?.findIndex((a) => a.isCurrent) ?? -1;
  const nextSubDasha =
    currentSubDashaIndex >= 0 && currentActiveDasha?.antardashas?.[currentSubDashaIndex + 1]
      ? currentActiveDasha.antardashas[currentSubDashaIndex + 1]
      : undefined;

  // Sub-period formatted dates
  const subDashaDateRange = currentSubDasha
    ? `${formatDate(currentSubDasha.startDate)} – ${formatDate(currentSubDasha.endDate)}`
    : '';

  const dashaDisplayText = currentActiveDasha
    ? `${currentActiveDasha.planet} Mahadasha${currentSubDasha ? ` • ${currentSubDasha.planet} Antardasha (${subDashaDateRange})` : ''}`
    : 'Running Dasha';

  // Calculate progress % in active sub-period
  const getSubDashaProgress = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const now = Date.now();
    if (isNaN(start) || isNaN(end) || end <= start) return 0;
    if (now <= start) return 0;
    if (now >= end) return 100;
    return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
  };

  const activeProgress = currentSubDasha ? getSubDashaProgress(currentSubDasha.startDate, currentSubDasha.endDate) : 0;

  // Chat states
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  // Dynamic analysis calculated directly from canonical Kundali (Instant Client-Side Canonical Baseline)
  const currentDynamicAnalysis = useMemo(() => {
    return generateDynamicAstrologicalAnalysis(
      kundali,
      inputMessage.trim() || 'General Vedic Chart Inquest',
      messages.map((m) => ({ sender: m.sender, text: m.text }))
    );
  }, [kundali, inputMessage, messages]);

  // Synchronize active forensic hashes with latest dynamic analysis calculation
  useEffect(() => {
    if (currentDynamicAnalysis?.forensicHashes) {
      setActiveForensicHashes(currentDynamicAnalysis.forensicHashes);
    }
  }, [currentDynamicAnalysis]);

  // Run Forensic Verification Suite
  const runForensicVerificationTest = async () => {
    setIsVerifying(true);
    try {
      const res = await fetch('/api/astrologer/forensic-verify', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setVerificationResult(data);
      }
    } catch (e) {
      console.error('Forensic test error:', e);
    } finally {
      setIsVerifying(false);
    }
  };

  // Initialize or update initial greeting when Kundali or activeAiLang changes
  useEffect(() => {
    const nativeName = kundali.birthDetails?.name || (isHindi ? 'जातक' : 'Native');
    const lagnaSign = kundali.houses?.[0]?.sign || 'Lagna';
    const mahaDashaPlanet = currentActiveDasha?.planet || 'Sun';
    const subDashaPlanet = currentSubDasha?.planet || 'Sun';

    let greeting = '';
    if (activeAiLang === 'hi') {
      greeting = `हरि ॐ, **${nativeName}**! मैं आपका वेदांग एआई ज्योतिषाचार्य हूँ। मैंने आपकी **${lagnaSign}** लग्न पत्रिका, सक्रिय **${mahaDashaPlanet} महादशा** एवं **${subDashaPlanet} अंतर्दशा (${subDashaDateRange})** का प्रामाणिक गणितीय अध्ययन किया है। आप शुद्ध हिन्दी, हिंग्लिश या मिश्रित भाषा में प्रश्न पूछ सकते हैं। आज आप किस विषय (करियर, विवाह, धन, स्वास्थ्य या वैदिक उपाय) पर मार्गदर्शन पाना चाहते हैं?`;
    } else if (activeAiLang === 'mr') {
      greeting = `हरि ॐ, **${nativeName}**! मी आपला वेदांग एआय ज्योतिषाचार्य आहे. मी आपल्या **${lagnaSign}** लग्न पत्रिकेचा, सक्रिय **${mahaDashaPlanet} महादशा** आणि **${subDashaPlanet} अंतर्दशा (${subDashaDateRange})** चा सखोल अभ्यास केला आहे. आज आपण करिअर, विवाह, संपत्ती किंवा वैदिक उपायांविषयी काय जाणून घेऊ इच्छिता?`;
    } else if (activeAiLang === 'ta') {
      greeting = `ஹரி ஓம், **${nativeName}**! நான் உங்கள் வேதாங்க ஜோதிட AI ஆச்சார்யா. உங்கள் **${lagnaSign}** லக்ன ஜாதகம், தற்போதைய **${mahaDashaPlanet} மகாதிசை** மற்றும் **${subDashaPlanet} அந்தர்திசை (${subDashaDateRange})** ஆகியவற்றை கணித்துள்ளேன். இன்று தொழில், திருமணம், செல்வம் அல்லது பரிகாரங்கள் குறித்து என்ன வழிகாட்டல் பெற விரும்புகிறீர்கள்?`;
    } else if (activeAiLang === 'te') {
      greeting = `హరి ఓం, **${nativeName}**! నేను మీ వేదాంగ జ్యోతిష్య AI ఆచార్యుడను. మీ **${lagnaSign}** లగ్న పత్రిక, ప్రస్తుతం నడుస్తున్న **${mahaDashaPlanet} మహర్దశ** మరియు **${subDashaPlanet} అంతర్దశ (${subDashaDateRange})** పరిశీలించాను. నేడు కెరీర్, వివాహం, సంపద లేదా వైదిక పరిహారాలపై మీకు ఏ మార్గదర్శనం కావాలి?`;
    } else if (activeAiLang === 'gu') {
      greeting = `હરિ ૐ, **${nativeName}**! હું આપનો વેદાંગ એઆઈ જ્યોતિષાચાર્ય છું. મેં આપની **${lagnaSign}** લગ્ન પત્રિકા, સક્રિય **${mahaDashaPlanet} મહાદશા** અને **${subDashaPlanet} અંતર્દશા (${subDashaDateRange})** નો સંપૂર્ણ અભ્યાસ કર્યો છે. આજે આપ કારકિર્દી, લગ્ન, ધન કે વૈદિક ઉપાયો અંગે શું માર્ગદર્શન મેળવવા માંગો છો?`;
    } else if (activeAiLang === 'bn') {
      greeting = `হরি ওঁ, **${nativeName}**! আমি আপনার বেদাঙ্গ এআই জ্যোতিষাচার্য। আপনার **${lagnaSign}** লগ্ন কুষ্ঠি, সক্রিয় **${mahaDashaPlanet} মহাদশা** এবং **${subDashaPlanet} অন্তর্দশা (${subDashaDateRange})** পুঙ্খানুপুঙ্খভাবে বিশ্লেষণ করেছি। আজ আপনি কর্মজীবন, বিবাহ, আর্থিক স্থিতি বা বৈদিক প্রতিকার নিয়ে কী পরামর্শ পেতে চান?`;
    } else {
      greeting = `Hari Om, **${nativeName}**! I am your VedangJyotisham AI Jyotish Acharya. I have calculated your **${lagnaSign}** Lagna chart, currently running **${mahaDashaPlanet} Mahadasha** and active **${subDashaPlanet} Antardasha (Sub-Period: ${subDashaDateRange})**. You may ask in English, Hinglish, or any regional language. How may I guide you today regarding career timing, relationships, wealth, or remedial upayas?`;
    }

    setMessages([
      {
        sender: 'astrologer',
        text: greeting
      }
    ]);
  }, [kundali, activeAiLang, currentActiveDasha, currentSubDasha, subDashaDateRange, isHindi]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  useEffect(() => {
    if (initialPrompt && !hasSentInitialPrompt) {
      setHasSentInitialPrompt(true);
      executeSendMessage(initialPrompt);
    }
  }, [initialPrompt, hasSentInitialPrompt]);

  // Generate full AI Report
  const handleGenerateAnalysis = async () => {
    setIsGeneratingAnalysis(true);
    setAnalysisError(null);
    try {
      const aiModel = localStorage.getItem('astro_ai_model') || 'gemini-3.7-flash';
      const customApiKey = localStorage.getItem('astro_custom_ai_key') || '';
      const aiTemp = parseFloat(localStorage.getItem('astro_ai_temp') || '0.7');
      const aiPrompt = localStorage.getItem('astro_ai_prompt') || '';

      const savedUserStr = localStorage.getItem('astro_current_user');
      let currentUserRole = 'guest';
      let currentUserId = undefined;
      if (savedUserStr) {
        try {
          const parsed = JSON.parse(savedUserStr);
          currentUserRole = parsed.role || 'user';
          currentUserId = parsed.email || parsed.uid || parsed.mobile;
        } catch (_) {}
      }

      const res = await fetch('/api/astrologer/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kundali,
          lang: activeAiLang,
          channel: 'web',
          userRole: currentUserRole,
          userId: currentUserId,
          aiConfig: {
            model: aiModel,
            apiKey: customApiKey,
            temperature: aiTemp,
            systemInstruction: aiPrompt
          }
        })
      });

      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data.forensicHashes) {
          setActiveForensicHashes(data.forensicHashes);
        }
        if (res.ok && data.analysis) {
          setAnalysisText(data.analysis);
        } else {
          setAnalysisError(data.error || (isHindi ? 'विश्लेषण उत्पन्न करने में असमर्थ।' : 'Unable to generate analysis at this time.'));
        }
      } else {
        const text = await res.text();
        console.error('Server returned non-JSON response:', text);
        setAnalysisError(isHindi ? 'सेवा अस्थायी रूप से अनुपलब्ध है। कृपया पुनः प्रयास करें।' : 'Service temporarily unavailable. Please retry in a moment.');
      }
    } catch (e: any) {
      console.error('AI Analysis error:', e);
      setAnalysisError(e.message || (isHindi ? 'एआई ज्योतिषाचार्य से संपर्क करने में त्रुटि।' : 'Error communicating with AI Astrologer.'));
    } finally {
      setIsGeneratingAnalysis(false);
    }
  };

  // Send Chat Message
  const executeSendMessage = async (textToSend: string) => {
    if (!textToSend.trim() || isSending) return;

    const rawUserText = textToSend.trim();
    const maskRes = maskSensitiveContactInfo(rawUserText);
    const userText = maskRes.maskedText;
    setInputMessage('');
    const updatedMessages: ChatMessage[] = [...messages, { sender: 'user', text: userText }];
    setMessages(updatedMessages);
    setIsSending(true);

    try {
      const aiModel = localStorage.getItem('astro_ai_model') || 'gemini-3.7-flash';
      const customApiKey = localStorage.getItem('astro_custom_ai_key') || '';
      const aiTemp = parseFloat(localStorage.getItem('astro_ai_temp') || '0.7');
      const aiPrompt = localStorage.getItem('astro_ai_prompt') || '';

      const savedUserStr = localStorage.getItem('astro_current_user');
      let currentUserRole = 'guest';
      let currentUserId = undefined;
      if (savedUserStr) {
        try {
          const parsed = JSON.parse(savedUserStr);
          currentUserRole = parsed.role || 'user';
          currentUserId = parsed.email || parsed.uid || parsed.mobile;
        } catch (_) {}
      }

      const res = await fetch('/api/astrologer/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userText,
          kundali,
          chatHistory: updatedMessages.slice(-8),
          lang: activeAiLang,
          channel: 'web',
          userRole: currentUserRole,
          userId: currentUserId,
          aiConfig: {
            model: aiModel,
            apiKey: customApiKey,
            temperature: aiTemp,
            systemInstruction: aiPrompt
          }
        })
      });

      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data.forensicHashes) {
          setActiveForensicHashes(data.forensicHashes);
        }
        if (res.ok && data.reply) {
          setMessages((prev) => [...prev, { sender: 'astrologer', text: data.reply }]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              sender: 'astrologer',
              text: isHindi
                ? `*क्षमा करें, ${data.error || 'ग्रह गोचर गणना में क्षणिक व्यवधान आया है। कृपया पुनः पूछें।'}*`
                : `*Apologies, ${data.error || 'I encountered a temporary disturbance in reading the celestial transits. Please ask again.'}*`
            }
          ]);
        }
      } else {
        const text = await res.text();
        console.error('Non-JSON chat response:', text);
        setMessages((prev) => [
          ...prev,
          {
            sender: 'astrologer',
            text: isHindi
              ? '*ज्योतिष सेवा से अप्रत्याशित प्रतिक्रिया मिली। कृपया पुनः प्रयास करें।*'
              : '*The Astrological service returned an unexpected response. Please try asking again.*'
          }
        ]);
      }
    } catch (e: any) {
      console.error('Chat error:', e);
      setMessages((prev) => [
        ...prev,
        {
          sender: 'astrologer',
          text: isHindi
            ? '*नेटवर्क संचार त्रुटि। कृपया इंटरनेट कनेक्शन जांचें और पुनः प्रयास करें।*'
            : '*Network communication error. Please check your connection and try again.*'
        }
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    await executeSendMessage(inputMessage);
  };

  return (
    <div className="space-y-6">
      {/* Top Banner / Multilingual Engine Header */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-astro-primary text-astro-accent font-bold text-2xl flex items-center justify-center shadow-md font-serif shrink-0">
            ॐ
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2.5 py-0.5 rounded-full">
                {isHindi ? 'वेदांग ज्योतिषम् बहुभाषी एआई इंजन' : 'VedangJyotisham Multilingual AI Engine'}
              </span>
              <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                {isHindi ? 'कैनोनिकल गणना • शून्य भय' : 'Canonical Calculation • Zero Fear'}
              </span>
            </div>
            <h3 className="text-xl font-bold text-astro-text font-serif mt-1">
              {isHindi ? 'व्यक्तिगत एआई कुण्डली परामर्श एवं दशा विश्लेषण' : 'Personalized AI Kundali & Dasha Consultation'}
            </h3>
            <p className="text-xs text-astro-secondary-text">
              {isHindi
                ? 'करियर, धन, विवाह, महादशा-अंतर्दशा तिथियां, 3-भाग मंत्र एवं मिश्रित भाषा (हिंग्लिश) में सहज परामर्श।'
                : 'Parashari analysis of Career, Wealth, Marriage, Sub-Period dates, 3-part Mantras & mixed multilingual queries.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto justify-end flex-wrap">
          <button
            onClick={() => setShowForensicDebug(!showForensicDebug)}
            className={`px-3 py-2.5 rounded-xl border text-xs font-semibold transition-colors flex items-center gap-1.5 cursor-pointer ${
              showForensicDebug
                ? 'bg-astro-primary text-white border-astro-primary'
                : 'bg-astro-card hover:bg-astro-border text-astro-primary border-astro-border'
            }`}
            title="Forensic Diagnostic Debug Mode & Cryptographic Hashes"
          >
            <Cpu className="w-4 h-4 text-astro-accent" />
            <span>{isHindi ? 'फॉरेंसिक डायग्नोस्टिक' : 'Forensic Hashes'}</span>
          </button>

          <button
            onClick={() => setShowEngineDirectives(!showEngineDirectives)}
            className="px-3.5 py-2.5 rounded-xl border border-astro-border bg-astro-card hover:bg-astro-border text-astro-primary font-semibold text-xs transition-colors flex items-center gap-1.5 cursor-pointer"
            title="View Engine Directives & Standards"
          >
            <Info className="w-4 h-4 text-astro-accent" />
            <span className="hidden sm:inline">{isHindi ? 'इंजन नियम' : 'AI Directives'}</span>
          </button>

          <button
            onClick={handleGenerateAnalysis}
            disabled={isGeneratingAnalysis}
            className="px-5 py-2.5 rounded-xl bg-astro-primary hover:bg-astro-primary/90 text-white font-bold text-xs shadow-md flex items-center gap-2 transition-all transform hover:scale-[1.02] shrink-0 disabled:opacity-50 cursor-pointer"
          >
            {isGeneratingAnalysis ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>{isHindi ? 'ग्रह स्थितियों का विश्लेषण जारी...' : 'Ephemeris Analysis...'}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-astro-accent" />
                <span>
                  {analysisText
                    ? (isHindi ? 'पुनः विश्लेषण' : 'Regenerate Reading')
                    : (isHindi ? 'सम्पूर्ण कुण्डली विश्लेषण' : 'Generate Full Reading')}
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Multilingual Consultation Language Bar */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-3.5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-astro-primary/10 rounded-lg text-astro-primary">
            <Languages className="w-4 h-4" />
          </div>
          <div>
            <span className="text-xs font-bold text-astro-primary">
              {isHindi ? 'परामर्श प्रस्तुति भाषा (Language Layer):' : 'AI Consultation Language Layer:'}
            </span>
            <p className="text-[10px] text-astro-secondary-text">
              {isHindi ? 'गणना अपरिवर्तित रहती है • किसी भी भाषा या हिंग्लिश में पूछें' : 'Calculations remain identical • Supports pure regional & mixed Hinglish'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1">
          {SUPPORTED_AI_LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => setActiveAiLang(l.code)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeAiLang === l.code
                  ? 'bg-astro-primary text-white font-bold shadow-sm'
                  : 'bg-astro-card text-astro-secondary-text hover:text-astro-text hover:bg-astro-border/70 border border-astro-border'
              }`}
            >
              {l.native}
            </button>
          ))}
        </div>
      </div>

      {/* 18-Point Canonical Engine Principles Modal / Collapsible Details */}
      {showEngineDirectives && (
        <div className="bg-astro-bg border-2 border-astro-primary/30 rounded-2xl p-5 shadow-md space-y-3 animate-in fade-in duration-200">
          <div className="flex items-center justify-between border-b border-astro-border pb-2">
            <div className="flex items-center gap-2 text-astro-primary font-bold font-serif text-sm">
              <Scale className="w-4 h-4 text-astro-accent" />
              <span>VEDANGJYOTISHAM — MULTILINGUAL ASTROLOGY AI ENGINE DIRECTIVES</span>
            </div>
            <button
              onClick={() => setShowEngineDirectives(false)}
              className="text-xs text-astro-secondary-text hover:text-red-700 font-semibold px-2 py-0.5 rounded-md hover:bg-astro-card"
            >
              ✕ Close
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-astro-text">
            <div className="bg-astro-card p-3 rounded-xl border border-astro-border space-y-1">
              <div className="font-bold text-astro-primary flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                1. Calculate Once, Present Naturally
              </div>
              <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                Language is purely a presentation layer. Underlying planetary mathematics, dasha timelines, and Vargas remain canonical and identical across languages.
              </p>
            </div>
            <div className="bg-astro-card p-3 rounded-xl border border-astro-border space-y-1">
              <div className="font-bold text-astro-primary flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                2. Mixed Language & Code-Switching
              </div>
              <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                Natural comprehension of Hinglish/mixed queries like <em>"Saturn 10th house mein hai, career effect?"</em> with no forced grammatical purity.
              </p>
            </div>
            <div className="bg-astro-card p-3 rounded-xl border border-astro-border space-y-1">
              <div className="font-bold text-astro-primary flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                3. Three-Part Mantra & Zero Fear
              </div>
              <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                Remedial mantras rendered with <strong>Original Sanskrit + Transliteration + Meaning</strong>. Strict anti-fear-mongering and safety boundaries.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Forensic Diagnostics & Hash Debug Panel */}
      {showForensicDebug && (
        <div className="bg-[#FFFEFB] dark:bg-[#1E2522] border border-[#E2DED5] dark:border-[#3B433F] rounded-2xl p-5 shadow-xs text-[#263633] dark:text-[#F2F1EB] space-y-4 animate-in fade-in duration-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#E2DED5] dark:border-[#3B433F] pb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-astro-secondary/10 dark:bg-[#86A7A0]/20 rounded-lg text-astro-secondary dark:text-[#86A7A0]">
                <Cpu className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-[#263633] dark:text-[#F2F1EB] tracking-wide flex items-center gap-2">
                  <span>VEDANG DYNAMIC ANALYSIS ENGINE — FORENSIC DIAGNOSTICS</span>
                  <span className="text-[10px] bg-astro-secondary/10 text-astro-secondary dark:bg-[#86A7A0]/20 dark:text-[#86A7A0] border border-astro-secondary/30 px-2 py-0.5 rounded-full font-mono font-semibold">
                    GROUND TRUTH PRE-COMPUTED
                  </span>
                </h4>
                <p className="text-[11px] text-[#52635F] dark:text-[#B9C0BB]">
                  Application calculates analysis first • AI only interprets dynamic data structures • Zero static prompt dependency
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowForensicDebug(false)}
              className="text-xs text-[#52635F] dark:text-[#B9C0BB] hover:text-[#263633] font-semibold px-2 py-1 rounded-md hover:bg-astro-secondary-surface dark:hover:bg-[#252D2A] self-start sm:self-auto cursor-pointer"
            >
              ✕ Close
            </button>
          </div>

          {/* Cryptographic Hash Matrix */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-astro-secondary dark:text-[#86A7A0]">
              <span className="flex items-center gap-1.5">
                <Fingerprint className="w-3.5 h-3.5" />
                Canonical Fingerprint Hashes (Real-Time Reactive Invalidation):
              </span>
              <span className="text-[10px] text-[#52635F] dark:text-[#B9C0BB] font-mono">
                Native: {activeForensicHashes?.nativeId || currentDynamicAnalysis.nativeId}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 font-mono text-[11px]">
              <div className="bg-[#F8F6F0] dark:bg-[#202724] p-2.5 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-astro-secondary dark:text-[#86A7A0] font-sans font-bold">NATIVE ID</span>
                <span className="text-[#263633] dark:text-[#F2F1EB] font-semibold truncate" title={activeForensicHashes?.nativeId || currentDynamicAnalysis.nativeId}>
                  {activeForensicHashes?.nativeId || currentDynamicAnalysis.nativeId}
                </span>
              </div>

              <div className="bg-[#F8F6F0] dark:bg-[#202724] p-2.5 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-astro-secondary dark:text-[#86A7A0] font-sans font-bold">CHART HASH</span>
                <span className="text-[#263633] dark:text-[#F2F1EB] font-semibold truncate" title={activeForensicHashes?.chartHash || currentDynamicAnalysis.forensicHashes.chartHash}>
                  {activeForensicHashes?.chartHash || currentDynamicAnalysis.forensicHashes.chartHash}
                </span>
              </div>

              <div className="bg-[#F8F6F0] dark:bg-[#202724] p-2.5 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-astro-secondary dark:text-[#86A7A0] font-sans font-bold">ANALYSIS HASH</span>
                <span className="text-[#263633] dark:text-[#F2F1EB] font-semibold truncate" title={activeForensicHashes?.analysisHash || currentDynamicAnalysis.forensicHashes.analysisHash}>
                  {activeForensicHashes?.analysisHash || currentDynamicAnalysis.forensicHashes.analysisHash}
                </span>
              </div>

              <div className="bg-[#F8F6F0] dark:bg-[#202724] p-2.5 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-astro-secondary dark:text-[#86A7A0] font-sans font-bold">QUESTION HASH</span>
                <span className="text-[#263633] dark:text-[#F2F1EB] font-semibold truncate" title={activeForensicHashes?.questionHash || currentDynamicAnalysis.forensicHashes.questionHash}>
                  {activeForensicHashes?.questionHash || currentDynamicAnalysis.forensicHashes.questionHash}
                </span>
              </div>

              <div className="bg-[#F8F6F0] dark:bg-[#202724] p-2.5 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-astro-secondary dark:text-[#86A7A0] font-sans font-bold">PERSONALITY HASH</span>
                <span className="text-[#263633] dark:text-[#F2F1EB] font-semibold truncate" title={activeForensicHashes?.personalityHash || currentDynamicAnalysis.forensicHashes.personalityHash}>
                  {activeForensicHashes?.personalityHash || currentDynamicAnalysis.forensicHashes.personalityHash}
                </span>
              </div>

              <div className="bg-[#F8F6F0] dark:bg-[#202724] p-2.5 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-astro-secondary dark:text-[#86A7A0] font-sans font-bold">LIFE AREA HASH</span>
                <span className="text-[#263633] dark:text-[#F2F1EB] font-semibold truncate" title={activeForensicHashes?.lifeAreaHash || currentDynamicAnalysis.forensicHashes.lifeAreaHash}>
                  {activeForensicHashes?.lifeAreaHash || currentDynamicAnalysis.forensicHashes.lifeAreaHash}
                </span>
              </div>

              <div className="bg-[#F8F6F0] dark:bg-[#202724] p-2.5 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-astro-secondary dark:text-[#86A7A0] font-sans font-bold">TIMING HASH</span>
                <span className="text-[#263633] dark:text-[#F2F1EB] font-semibold truncate" title={activeForensicHashes?.timingHash || currentDynamicAnalysis.forensicHashes.timingHash}>
                  {activeForensicHashes?.timingHash || currentDynamicAnalysis.forensicHashes.timingHash}
                </span>
              </div>

              <div className="bg-[#F8F6F0] dark:bg-[#202724] p-2.5 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] flex flex-col justify-between">
                <span className="text-[9px] uppercase tracking-wider text-astro-secondary dark:text-[#86A7A0] font-sans font-bold">EVIDENCE HASH</span>
                <span className="text-[#263633] dark:text-[#F2F1EB] font-semibold truncate" title={activeForensicHashes?.evidenceHash || currentDynamicAnalysis.forensicHashes.evidenceHash}>
                  {activeForensicHashes?.evidenceHash || currentDynamicAnalysis.forensicHashes.evidenceHash}
                </span>
              </div>
            </div>
          </div>

          {/* Dynamic Evidence Summary */}
          <div className="bg-[#F8F6F0] dark:bg-[#202724] p-3 rounded-xl border border-[#E2DED5] dark:border-[#3B433F] text-xs space-y-2">
            <div className="flex items-center justify-between text-[11px] font-semibold text-astro-secondary dark:text-[#86A7A0] border-b border-[#E2DED5] dark:border-[#3B433F] pb-1.5">
              <span>Active Dynamic Model Summary:</span>
              <span className="text-astro-accent font-mono">
                Domain: {currentDynamicAnalysis.questionClassification.primaryDomain.toUpperCase()} | Timing: {currentDynamicAnalysis.questionClassification.isTimingRequested ? 'Active' : 'Natal'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-[#263633] dark:text-[#F2F1EB]">
              <div>
                <strong className="text-astro-secondary dark:text-[#86A7A0]">Calculated Strongest Window:</strong>{' '}
                {currentDynamicAnalysis.timingModel.strongestWindow?.dateRange || 'Active Sub-Period'} ({currentDynamicAnalysis.timingModel.strongestWindow?.label || 'Direct Alignment'})
              </div>
              <div>
                <strong className="text-emerald-400">Natal Promise Verdict:</strong>{' '}
                {currentDynamicAnalysis.domains[currentDynamicAnalysis.questionClassification.primaryDomain]?.promiseVerdictEn || currentDynamicAnalysis.synthesisVerdictEn}
              </div>
            </div>
          </div>

          {/* Test Runner Suite */}
          <div className="pt-2 border-t border-astro-accent/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-xs text-emerald-200/80">
              <strong>Two-Native & Two-Question Verification Suite</strong>: Proves dynamic calculation differentiates both distinct natives and distinct questions with zero hash collision.
            </div>

            <button
              onClick={runForensicVerificationTest}
              disabled={isVerifying}
              className="px-4 py-2 rounded-xl bg-astro-accent hover:bg-astro-accent text-[#102322] font-bold text-xs shadow transition-all flex items-center gap-1.5 shrink-0 disabled:opacity-50 cursor-pointer"
            >
              {isVerifying ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Executing Verification Suite...</span>
                </>
              ) : (
                <>
                  <Terminal className="w-3.5 h-3.5" />
                  <span>Run Verification Test</span>
                </>
              )}
            </button>
          </div>

          {/* Verification Results Display */}
          {verificationResult && (
            <div className="bg-[#081211] p-3.5 rounded-xl border border-emerald-600/50 space-y-2.5 font-mono text-xs">
              <div className="flex items-center justify-between">
                <span className="font-bold text-emerald-300 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  VERIFICATION TEST RESULTS — {verificationResult.passed ? 'PASSED (100% DYNAMIC CERTIFIED)' : 'FAILED'}
                </span>
                <span className="text-[10px] text-emerald-400 bg-emerald-950 px-2 py-0.5 rounded border border-emerald-700">
                  {verificationResult.summary}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                {/* Two-Native Diff */}
                <div className="bg-[#0e201e] p-2.5 rounded-lg border border-emerald-900/60 space-y-1">
                  <div className="font-bold text-astro-accent">1. Two-Native Test (Different Horoscopes):</div>
                  <div className="text-emerald-200 truncate">
                    • Native A (Aries): <span className="text-emerald-400">{verificationResult.nativeA_Q1_Hash?.chartHash?.substring(0, 16)}...</span>
                  </div>
                  <div className="text-emerald-200 truncate">
                    • Native B (Libra): <span className="text-emerald-400">{verificationResult.nativeB_Q1_Hash?.chartHash?.substring(0, 16)}...</span>
                  </div>
                  <div className="text-emerald-300 font-semibold pt-1 border-t border-emerald-900/40">
                    Differentiation: Chart Hash {verificationResult.assertions?.chartHashesDistinct ? '✓ 100% Unique' : '✗ Failed'} | Analysis Hash {verificationResult.assertions?.analysisHashesDistinct ? '✓ 100% Unique' : '✗ Failed'}
                  </div>
                </div>

                {/* Two-Question Diff */}
                <div className="bg-[#0e201e] p-2.5 rounded-lg border border-emerald-900/60 space-y-1">
                  <div className="font-bold text-astro-accent">2. Two-Question Test (Different Domains on Native A):</div>
                  <div className="text-emerald-200 truncate">
                    • Q1 (Job Change): Timing Hash <span className="text-emerald-400">{verificationResult.nativeA_Q1_Hash?.timingHash?.substring(0, 16)}...</span>
                  </div>
                  <div className="text-emerald-200 truncate">
                    • Q2 (Marriage): Timing Hash <span className="text-emerald-400">{verificationResult.nativeA_Q2_Hash?.timingHash?.substring(0, 16)}...</span>
                  </div>
                  <div className="text-emerald-300 font-semibold pt-1 border-t border-emerald-900/40">
                    Differentiation: Timing Hash {verificationResult.assertions?.questionHashesDistinct ? '✓ 100% Unique' : '✗ Failed'} | Evidence Hash {verificationResult.assertions?.evidenceHashesDistinct ? '✓ 100% Unique' : '✗ Failed'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prominent Active Dasha & Sub-Period (Antardasha) Schedule Card */}
      {currentActiveDasha && (
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-3">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-astro-primary/10 text-astro-primary">
                <Calendar className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-astro-primary font-serif flex items-center gap-2">
                  <span>{isHindi ? 'सक्रिय विंशोत्तरी दशा एवं उप-अवधि (अंतर्दशा)' : 'Active Vimshottari Dasha & Sub-Period (Antardasha)'}</span>
                </h4>
                <p className="text-[11px] text-astro-secondary-text">
                  {isHindi ? 'सटीक उप-अवधि तिथियां एवं कालावधि' : 'Real-time temporal mapping of active astrological windows'}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowAllSubPeriods(!showAllSubPeriods)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-astro-primary bg-astro-card hover:bg-astro-border/60 px-3 py-1.5 rounded-xl border border-astro-border transition-colors self-start sm:self-auto cursor-pointer"
            >
              <Clock className="w-3.5 h-3.5 text-astro-accent" />
              <span>
                {showAllSubPeriods
                  ? (isHindi ? 'अंतर्दशा सूची छुपाएं' : 'Hide Sub-Period List')
                  : (isHindi ? `सभी ${currentActiveDasha.planet} अंतर्दशा तिथियां देखें` : `View All ${currentActiveDasha.planet} Sub-Period Dates`)}
              </span>
              {showAllSubPeriods ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>

          {/* Active Period Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Running Mahadasha Box */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 flex flex-col justify-between space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  {isHindi ? 'सक्रिय महादशा' : 'Running Mahadasha'}
                </span>
                <span className="text-[10px] font-bold bg-astro-primary text-white px-2 py-0.5 rounded-full">
                  {currentActiveDasha.durationYears} {isHindi ? 'वर्ष' : 'Years'}
                </span>
              </div>
              <div>
                <span className="text-lg font-bold text-astro-primary font-serif">
                  {currentActiveDasha.planet} {isHindi ? 'महादशा' : 'Mahadasha'}
                </span>
                <div className="text-xs text-astro-text font-medium mt-0.5 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-astro-secondary-text" />
                  <span>
                    {formatDate(currentActiveDasha.startDate)} — {formatDate(currentActiveDasha.endDate)}
                  </span>
                </div>
              </div>
            </div>

            {/* Active Sub-Period (Antardasha) Box */}
            <div className="bg-astro-primary/5 border border-astro-primary/20 rounded-xl p-4 flex flex-col justify-between space-y-2 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                  {isHindi ? 'सक्रिय उप-अवधि (अंतर्दशा)' : 'Current Active Sub-Period (Antardasha)'}
                </span>
                {currentSubDasha && (
                  <span className="text-[10px] font-bold bg-emerald-700 text-white px-2 py-0.5 rounded-full">
                    {currentSubDasha.durationYears} {isHindi ? 'वर्ष' : 'Yrs'}
                  </span>
                )}
              </div>

              {currentSubDasha ? (
                <div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-lg font-bold text-astro-primary font-serif">
                      {currentSubDasha.planet} {isHindi ? 'अंतर्दशा' : 'Antardasha'}
                    </span>
                    <span className="text-[11px] font-semibold text-astro-primary">
                      {activeProgress}% {isHindi ? 'व्यतीत' : 'Elapsed'}
                    </span>
                  </div>

                  {/* Sub-Period Date Display */}
                  <div className="text-xs font-semibold text-astro-primary mt-0.5 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-astro-accent" />
                    <span>
                      {formatDate(currentSubDasha.startDate)} — {formatDate(currentSubDasha.endDate)}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-astro-border h-1.5 rounded-full mt-2 overflow-hidden">
                    <div
                      className="bg-astro-primary h-full rounded-full transition-all duration-500"
                      style={{ width: `${activeProgress}%` }}
                    />
                  </div>

                  {nextSubDasha && (
                    <p className="text-[10px] text-astro-secondary-text mt-2 flex items-center gap-1">
                      <span>{isHindi ? 'अगली अंतर्दशा:' : 'Next Sub-Period:'}</span>
                      <strong className="text-astro-text font-semibold">
                        {nextSubDasha.planet} ({formatDate(nextSubDasha.startDate)} – {formatDate(nextSubDasha.endDate)})
                      </strong>
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-xs text-astro-secondary-text">
                  {isHindi ? 'उप-अवधि गणना उपलब्ध नहीं है' : 'Sub-period data not available'}
                </div>
              )}
            </div>
          </div>

          {/* Expandable Table of All 9 Sub-Period Dates for the Running Mahadasha */}
          {showAllSubPeriods && currentActiveDasha.antardashas && currentActiveDasha.antardashas.length > 0 && (
            <div className="mt-4 pt-4 border-t border-astro-border space-y-3">
              <div className="flex items-center justify-between">
                <h5 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                  {isHindi
                    ? `${currentActiveDasha.planet} महादशा की सभी अंतर्दशा (Sub-Period) तिथियां`
                    : `Complete Sub-Period (Antardasha) Schedule for ${currentActiveDasha.planet} Mahadasha`}
                </h5>
                <span className="text-[10px] text-astro-secondary-text">
                  {isHindi ? '9 अंतर्दशा चरण' : '9 Sequential Sub-Periods'}
                </span>
              </div>

              <div className="overflow-x-auto rounded-xl border border-astro-border">
                <table className="w-full text-left text-xs">
                  <thead className="bg-astro-card text-astro-secondary-text border-b border-astro-border font-semibold">
                    <tr>
                      <th className="py-2.5 px-3">{isHindi ? 'उप-ग्रह (Antardasha)' : 'Sub-Period Lord'}</th>
                      <th className="py-2.5 px-3">{isHindi ? 'प्रारंभ तिथि' : 'Start Date'}</th>
                      <th className="py-2.5 px-3">{isHindi ? 'समाप्ति तिथि' : 'End Date'}</th>
                      <th className="py-2.5 px-3">{isHindi ? 'अवधि' : 'Duration'}</th>
                      <th className="py-2.5 px-3">{isHindi ? 'स्थिति' : 'Status'}</th>
                      <th className="py-2.5 px-3 text-right">{isHindi ? 'परामर्श' : 'Consult'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-astro-border bg-astro-bg">
                    {currentActiveDasha.antardashas.map((ad, i) => {
                      const isPast = new Date(ad.endDate).getTime() < Date.now();
                      return (
                        <tr
                          key={i}
                          className={`transition-colors ${
                            ad.isCurrent
                              ? 'bg-astro-primary/10 font-bold'
                              : isPast
                              ? 'opacity-70 bg-transparent'
                              : 'hover:bg-astro-card/50'
                          }`}
                        >
                          <td className="py-2 px-3 font-semibold text-astro-primary flex items-center gap-1.5">
                            {ad.isCurrent && <span className="w-2 h-2 rounded-full bg-emerald-600" />}
                            <span>{ad.planet}</span>
                          </td>
                          <td className="py-2 px-3 text-astro-text font-mono text-[11px]">
                            {formatDate(ad.startDate)}
                          </td>
                          <td className="py-2 px-3 text-astro-text font-mono text-[11px]">
                            {formatDate(ad.endDate)}
                          </td>
                          <td className="py-2 px-3 text-astro-secondary-text">
                            {ad.durationYears} {isHindi ? 'वर्ष' : 'yrs'}
                          </td>
                          <td className="py-2 px-3">
                            {ad.isCurrent ? (
                              <span className="text-[10px] bg-emerald-600 text-white font-bold px-2 py-0.5 rounded-full">
                                {isHindi ? 'वर्तमान में सक्रिय' : 'Active Now'}
                              </span>
                            ) : isPast ? (
                              <span className="text-[10px] text-astro-secondary-text bg-astro-border/60 px-2 py-0.5 rounded-full">
                                {isHindi ? 'व्यतीत' : 'Completed'}
                              </span>
                            ) : (
                              <span className="text-[10px] text-astro-primary bg-astro-primary/10 px-2 py-0.5 rounded-full">
                                {isHindi ? 'आगामी' : 'Upcoming'}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <button
                              onClick={() => {
                                const question = isHindi
                                  ? `${currentActiveDasha.planet} महादशा में ${ad.planet} अंतर्दशा (${formatDate(ad.startDate)} से ${formatDate(ad.endDate)}) के दौरान मेरे जीवन और करियर पर क्या प्रभाव पड़ेगा?`
                                  : `What specific life and career shifts can I expect during ${currentActiveDasha.planet}-${ad.planet} Antardasha (${formatDate(ad.startDate)} to ${formatDate(ad.endDate)})?`;
                                executeSendMessage(question);
                              }}
                              className="text-[11px] text-astro-primary hover:text-astro-primary/80 hover:underline font-semibold cursor-pointer"
                            >
                              {isHindi ? 'पूछें' : 'Ask'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Quick Sub-Period Consultation Chips */}
          {currentSubDasha && (
            <div className="pt-2 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold text-astro-secondary-text flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-astro-accent" />
                {isHindi ? 'त्वरित परामर्श प्रश्न:' : 'Quick Sub-Period Inquiries:'}
              </span>

              <button
                onClick={() => {
                  const q = isHindi
                    ? `मेरी वर्तमान ${currentSubDasha.planet} अंतर्दशा (${formatDate(currentSubDasha.startDate)} से ${formatDate(currentSubDasha.endDate)}) में करियर एवं आर्थिक स्थिति का क्या भविष्यफल है?`
                    : `What is the career and financial forecast for my current ${currentSubDasha.planet} Antardasha (${formatDate(currentSubDasha.startDate)} to ${formatDate(currentSubDasha.endDate)})?`;
                  executeSendMessage(q);
                }}
                className="text-xs bg-astro-card hover:bg-astro-border/70 text-astro-primary font-medium px-3 py-1.5 rounded-xl border border-astro-border transition-colors flex items-center gap-1 cursor-pointer"
              >
                <span>
                  {isHindi
                    ? `वर्तमान ${currentSubDasha.planet} अंतर्दशा फल (${formatDate(currentSubDasha.startDate)} - ${formatDate(currentSubDasha.endDate)})`
                    : `Current ${currentSubDasha.planet} Sub-Period Impact (${formatDate(currentSubDasha.startDate)} - ${formatDate(currentSubDasha.endDate)})`}
                </span>
                <ArrowRight className="w-3 h-3 text-astro-accent" />
              </button>

              <button
                onClick={() => {
                  const q = isHindi
                    ? `मेरी वर्तमान ${currentSubDasha.planet} अंतर्दशा (${formatDate(currentSubDasha.startDate)} से ${formatDate(currentSubDasha.endDate)}) के लिए सर्वोत्तम वैदिक उपाय एवं मंत्र क्या हैं?`
                    : `What are the most auspicious Vedic remedies and mantras for my current ${currentSubDasha.planet} Antardasha (${formatDate(currentSubDasha.startDate)} to ${formatDate(currentSubDasha.endDate)})?`;
                  executeSendMessage(q);
                }}
                className="text-xs bg-astro-card hover:bg-astro-border/70 text-astro-primary font-medium px-3 py-1.5 rounded-xl border border-astro-border transition-colors flex items-center gap-1 cursor-pointer"
              >
                <span>
                  {isHindi ? `उपाय (${currentSubDasha.planet} अंतर्दशा)` : `Remedies for ${currentSubDasha.planet} Sub-Period`}
                </span>
                <ArrowRight className="w-3 h-3 text-astro-accent" />
              </button>

              {nextSubDasha && (
                <button
                  onClick={() => {
                    const q = isHindi
                      ? `मेरी अगली ${nextSubDasha.planet} अंतर्दशा (${formatDate(nextSubDasha.startDate)} से ${formatDate(nextSubDasha.endDate)}) में क्या बदलाव आएंगे?`
                      : `What major shifts should I prepare for in my upcoming ${nextSubDasha.planet} Antardasha (${formatDate(nextSubDasha.startDate)} to ${formatDate(nextSubDasha.endDate)})?`;
                    executeSendMessage(q);
                  }}
                  className="text-xs bg-astro-card hover:bg-astro-border/70 text-astro-primary font-medium px-3 py-1.5 rounded-xl border border-astro-border transition-colors flex items-center gap-1 cursor-pointer"
                >
                  <span>
                    {isHindi
                      ? `आगामी ${nextSubDasha.planet} अंतर्दशा (${formatDate(nextSubDasha.startDate)} से)`
                      : `Upcoming ${nextSubDasha.planet} (${formatDate(nextSubDasha.startDate)})`}
                  </span>
                  <ArrowRight className="w-3 h-3 text-astro-accent" />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Content Area: AI Analysis Report or Chat interface */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Full AI Analysis Markdown Report */}
        <div className="lg:col-span-7 bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-astro-border pb-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-astro-primary" />
              <h4 className="font-bold text-astro-primary font-serif text-base">
                {isHindi ? 'मास्टर ज्योतिषीय विश्लेषण रिपोर्ट' : 'Master Astrological Analysis'}
              </h4>
            </div>
            {analysisText && (
              <span className="text-[10px] text-astro-secondary-text bg-astro-card px-2.5 py-1 rounded-full border border-astro-border">
                {isHindi ? 'एआई पराशरीय रिपोर्ट' : 'AI Parashari Report'}
              </span>
            )}
          </div>

          {analysisError && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-700 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <span>{analysisError}</span>
            </div>
          )}

          {analysisText ? (
            <div className="markdown-body max-w-none text-xs text-astro-text leading-relaxed max-h-[580px] overflow-y-auto pr-2 space-y-3">
              <ReactMarkdown>{analysisText}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-center py-16 space-y-4 text-astro-secondary-text">
              <Sparkles className="w-12 h-12 text-astro-primary/50 mx-auto animate-pulse" />
              <p className="text-xs max-w-md mx-auto leading-relaxed">
                {isHindi ? (
                  <>
                    अपनी विस्तृत वैदिक ज्योतिष रिपोर्ट जनरेट करने के लिए ऊपर <span className="text-astro-primary font-bold">"सम्पूर्ण कुण्डली विश्लेषण प्राप्त करें"</span> पर क्लिक करें। इसमें सटीक महादशा एवं अंतर्दशा तिथियां शामिल होंगी।
                  </>
                ) : (
                  <>
                    Click <span className="text-astro-primary font-bold">"Generate Comprehensive Reading"</span> above to calculate your complete AI Parashari Vedic Astrology Report including exact Dasha and Sub-Period timelines.
                  </>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Interactive Chat Assistant */}
        <div className="lg:col-span-5 bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col h-[650px]">
          <div className="flex items-center justify-between border-b border-astro-border pb-3 shrink-0">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-astro-primary" />
              <div>
                <h4 className="font-bold text-astro-primary font-serif text-sm">
                  {isHindi ? 'ज्योतिष आचार्य से परामर्श लें' : 'Ask Jyotish Acharya'}
                </h4>
                <p className="text-[10px] text-astro-secondary-text">
                  {isHindi ? 'आपकी जन्म कुण्डली एवं अंतर्दशा तिथियों पर आधारित' : 'Grounded in your chart & sub-period dates'}
                </p>
              </div>
            </div>
            {currentSubDasha && (
              <span className="text-[10px] bg-astro-primary/10 text-astro-primary font-bold px-2 py-0.5 rounded-full border border-astro-primary/20">
                {currentSubDasha.planet} {isHindi ? 'अंतर्दशा' : 'Sub-Period'}
              </span>
            )}
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto py-3 space-y-3 pr-1 text-xs">
            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-2 ${m.sender === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div
                  className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                    m.sender === 'user'
                      ? 'bg-astro-primary text-white'
                      : 'bg-astro-card text-astro-primary border border-astro-border'
                  }`}
                >
                  {m.sender === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                </div>

                <div
                  className={`p-3 rounded-2xl max-w-[85%] text-xs leading-relaxed ${
                    m.sender === 'user'
                      ? 'bg-astro-primary text-white rounded-tr-none'
                      : 'bg-astro-card text-astro-text border border-astro-border rounded-tl-none'
                  }`}
                >
                  <div className="markdown-body">
                    <ReactMarkdown>{m.text}</ReactMarkdown>
                  </div>
                </div>
              </div>
            ))}

            {isSending && (
              <div className="flex items-center gap-2 text-astro-secondary-text text-xs italic">
                <Bot className="w-4 h-4 text-astro-primary animate-pulse" />
                <span>{isHindi ? 'ज्योतिष आचार्य ग्रह गोचर एवं अंतर्दशा कालखण्ड गणना कर रहे हैं...' : 'Jyotish Acharya is calculating planetary transits & sub-period timeline...'}</span>
              </div>
            )}
            <div ref={chatBottomRef} />
          </div>

          {/* Chat Input Box */}
          <form onSubmit={handleSendMessage} className="pt-3 border-t border-astro-border flex items-center gap-2 shrink-0">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={isHindi ? 'शुद्ध हिन्दी, हिंग्लिश या मिश्रित भाषा में पूछें (उदा: "Saturn 10th house mein kaisa hai?")...' : 'Ask in English, Hindi, Hinglish or mixed language (e.g. "Meri Shani dasha kab tak hai?")...'}
              className="flex-1 bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-3.5 py-2.5 text-xs text-astro-text focus:outline-none placeholder:text-astro-secondary-text"
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || isSending}
              className="p-2.5 rounded-xl bg-astro-primary hover:bg-astro-primary/90 text-white disabled:opacity-50 transition-all shrink-0 cursor-pointer"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

